// Town marketing pages — social media, PPC, reviews and Google Maps for the
// 60-mile towns. Each town's combined /web-design-seo-{town} page stays the
// owner of web design + SEO intent; these pages own the broader "marketing"
// intent so the two never compete for the same search.
//
// Configs reuse the KeywordServicePage shape and KeywordServiceLanding layout.

import type { KeywordServicePage } from "@/lib/keywordServicePages";

type TownSeed = {
  slug: string;
  name: string;
  county: string;
  context: string;
  towns: string;
};

function townMarketingPage(seed: TownSeed): KeywordServicePage {
  const path = `/marketing-${seed.slug}`;
  const townPage = `/web-design-seo-${seed.slug}`;
  return {
    path,
    title: `${seed.name} Marketing: Social Media, PPC, Reviews & Maps | Colorado Web Impressions`,
    description: `Marketing for ${seed.name}, CO businesses: social media, Google Ads, review generation and Google Maps from our Colorado Springs office. From $950/month, month-to-month.`,
    h1: `Marketing for ${seed.name}, Colorado businesses`,
    intro: `A website and rankings are the foundation — but ${seed.name} businesses also win customers through social media, Google Ads, steady reviews and a well-kept Google Maps profile. Colorado Web Impressions runs all of it from one real office on Woodmen Rd in Colorado Springs, with Digital Marketing Coach Chris Heidlebaugh's 27 years of experience. ${seed.context} Marketing starts at $950/month, month-to-month, with published pricing.`,
    service: "seo",
    article: townPage,
    articleLabel: `Web design & SEO in ${seed.name}`,
    sections: [
      {
        heading: `Social media that shows real ${seed.name} work`,
        body: `Finished jobs, happy customers and quick tips from ${seed.towns} build more trust than generic graphics. We plan and publish content for Facebook, Instagram and LinkedIn that reflects your actual work, and every draft needs your approval before it goes out. Social media marketing is included in Growth and Expert marketing packages.`,
      },
      {
        heading: "Google Ads and PPC without wasted spend",
        body: `Paid ads can fill the schedule while organic rankings build. We set locations to people actually in ${seed.name} and your service area, tighten keywords, track real calls and form fills, and send clicks to the right page — not your homepage. PPC management is part of the Expert package at $2,750/month; ad spend itself is never included in any package.`,
      },
      {
        heading: "Reviews that compound",
        body: `In a market like ${seed.name}, a steady flow of genuine Google reviews is often the difference between getting the call and being skipped. We build a simple, repeatable review-request process for your customers and help you reply to every review. Reputation management is included in the Expert package. We never invent or buy reviews.`,
      },
      {
        heading: "Google Maps as your front door",
        body: `Most local calls start in the Google Maps box. We keep your categories, services, hours and photos accurate, post regular updates, and make sure your website and profile tell the same story about where you work — ${seed.towns}. Google Maps optimization is included in every marketing package, starting with Starter at $950/month.`,
      },
      {
        heading: "One team, one bill, no contracts",
        body: `Instead of juggling a social freelancer, an ads agency and an SEO consultant, ${seed.name} businesses get one owner-led team. Starter ($950/mo) covers Local SEO and Google Maps; Growth ($1,650/mo) adds AI search optimization and social media; Expert ($2,750/mo) adds multi-city SEO, PPC and reputation management. Every engagement is month-to-month.`,
      },
    ],
    faqs: [
      { q: `Do you actually work with ${seed.name} businesses?`, a: `Yes. ${seed.name} is inside our 60-mile service area around our Colorado Springs office at 3210 E Woodmen Rd. We can meet in person or by video.` },
      { q: "How much does marketing cost?", a: "Starter is $950/month (Local SEO + Google Maps), Growth is $1,650/month (adds AI search and social media), and Expert is $2,750/month (adds multi-city SEO, PPC and reputation management). Ad spend is separate. Month-to-month, no contracts." },
      { q: "Can you just run my Google Ads?", a: `PPC management is part of the Expert package rather than a standalone service, because ads work best when the website, Maps profile and reviews behind them are strong too.` },
      { q: `How fast will marketing bring customers in ${seed.name}?`, a: `Smaller ${seed.county} markets often move faster than big cities, but timing depends on your starting point, competition and season. We never guarantee rankings, a number of leads, or a deadline.` },
      { q: `Do I need a new website first?`, a: `Not always. We review your current site first — if it converts visitors well, we market what you have. If it holds you back, websites start at $1,850 one-time.` },
    ],
  };
}

export const TOWN_MARKETING_PAGES: KeywordServicePage[] = [
  townMarketingPage({
    slug: "peyton",
    name: "Peyton",
    county: "El Paso County",
    context: "Rural acreage and new subdivisions along the Highway 24 corridor mean Peyton customers search for businesses willing to come to them — your marketing needs to make that obvious.",
    towns: "Peyton, Falcon, Calhan and the Highway 24 corridor",
  }),
  townMarketingPage({
    slug: "calhan",
    name: "Calhan",
    county: "El Paso County",
    context: "In a small town like Calhan, reputation travels fast — steady Google reviews and an active Maps profile matter even more than in the city.",
    towns: "Calhan, Ramah, Simla and eastern El Paso County",
  }),
  townMarketingPage({
    slug: "ellicott",
    name: "Ellicott",
    county: "El Paso County",
    context: "Ellicott sits between Colorado Springs and the eastern plains, so your marketing needs to capture searches from both directions.",
    towns: "Ellicott, Calhan, Peyton and the Highway 94 corridor",
  }),
  townMarketingPage({
    slug: "pueblo-west",
    name: "Pueblo West",
    county: "Pueblo County",
    context: "Pueblo West is one of the fastest-growing communities in southern Colorado, and new residents search Google and Maps before they ask neighbors.",
    towns: "Pueblo West, Pueblo and the Highway 50 corridor",
  }),
  townMarketingPage({
    slug: "colorado-city",
    name: "Colorado City",
    county: "Pueblo County",
    context: "Colorado City businesses serve both locals and I-25 travelers heading between Pueblo and Walsenburg — Maps visibility captures both.",
    towns: "Colorado City, Rye and the Greenhorn Valley",
  }),
  townMarketingPage({
    slug: "castle-pines",
    name: "Castle Pines",
    county: "Douglas County",
    context: "Castle Pines is an affluent, established market where homeowners research carefully — reviews and a professional social presence carry real weight.",
    towns: "Castle Pines, Castle Rock and Lone Tree",
  }),
  townMarketingPage({
    slug: "larkspur",
    name: "Larkspur",
    county: "Douglas County",
    context: "Larkspur sits between Colorado Springs and the south Denver metro, so your marketing can pull customers from both directions along I-25.",
    towns: "Larkspur, Monument, Castle Rock and Perry Park",
  }),
  townMarketingPage({
    slug: "lone-tree",
    name: "Lone Tree",
    county: "Douglas County",
    context: "Lone Tree is a dense, high-income south-metro market where competition is strong — reviews, Maps and targeted ads decide who gets the call.",
    towns: "Lone Tree, Highlands Ranch, Centennial and Parker",
  }),
];

export function getTownMarketingPage(slug: string): KeywordServicePage {
  const page = TOWN_MARKETING_PAGES.find((p) => p.path === `/marketing-${slug}`);
  if (!page) throw new Error(`Unknown town marketing page: ${slug}`);
  return page;
}
